/**
 * The computed views: timeline, dashboard, and their public read-only twins (§5).
 *
 * These are the routes that run the projection engine server-side. The client
 * keeps the *display* logic but sources occurrences from here, so the server is
 * the single source of truth for the forecast (§8).
 */

import { z } from 'zod';

import type { Occurrence } from '@svcdue/core';

import { carDto, eventDto } from './entities.js';
import { eventStatus, status } from './enums.js';
import { isoDate, uuid } from './primitives.js';

/**
 * Mirrors `Occurrence` from @svcdue/core. The `satisfies` check below is what
 * keeps it honest: add a field to the engine's occurrence and this file stops
 * compiling until the wire format catches up.
 */
export const occurrence = z.object({
  occ: z.number().int(),
  total: z.number().int(),
  date: isoDate,
  governedBy: z.enum(['passes', 'miles', 'events', 'time']),
  firm: z.boolean(),
  overdue: z.boolean(),
  eventId: z.string().nullable(),
  atPass: z.number().int().nullable(),
  ofPasses: z.number().int().nullable(),
});

type OccurrenceContract = z.infer<typeof occurrence>;
// Fails to compile if the engine's Occurrence and this schema drift apart.
type _AssertOccurrenceMatches = OccurrenceContract extends Occurrence
  ? Occurrence extends OccurrenceContract
    ? true
    : { error: 'engine Occurrence has a field the contract is missing' }
  : { error: 'contract has a field the engine Occurrence does not' };
const _occurrenceMatches: _AssertOccurrenceMatches = true;
void _occurrenceMatches;

/** An occurrence plus what the timeline tile needs to label it. */
export const timelineOccurrence = occurrence.extend({
  itemId: uuid,
  itemName: z.string(),
  /** "Street Fox" or "Driver · Mark" — who this hangs off. */
  who: z.string(),
  carId: uuid.nullable(),
  /** "20 passes / 3,000 mi" — the interval, spelled out. */
  label: z.string(),
  /** True when it crosses mid-event, so the UI offers before/during. */
  choice: z.boolean(),
});
export type TimelineOccurrence = z.infer<typeof timelineOccurrence>;

/** A date-certain expiration or recert — computed from dates, not events. */
export const certOccurrence = z.object({
  itemId: uuid,
  itemName: z.string(),
  who: z.string(),
  carId: uuid.nullable(),
  date: isoDate,
  why: z.string(),
  status,
  daysUntil: z.number().int(),
});
export type CertOccurrence = z.infer<typeof certOccurrence>;

/** An inspect-by-hand item, with its window resolved to a status. */
export const inspection = z.object({
  itemId: uuid,
  itemName: z.string(),
  partNumber: z.string().nullable(),
  who: z.string(),
  carId: z.string().nullable(),
  note: z.string(),
  windowDays: z.number().int(),
  lastCheckedAt: isoDate.nullable(),
  nextDue: isoDate,
  daysUntil: z.number().int(),
  status,
});
export type Inspection = z.infer<typeof inspection>;

export const timelineResponse = z.object({
  events: z.array(eventDto),
  occurrences: z.array(timelineOccurrence),
  certs: z.array(certOccurrence),
  inspections: z.array(inspection),
  /** The date the forecast was computed for — so the client renders "12d out" consistently. */
  today: isoDate,
});
export type TimelineResponse = z.infer<typeof timelineResponse>;

export const timelineQuery = z.object({
  carId: uuid.optional(),
});
export type TimelineQuery = z.infer<typeof timelineQuery>;

/** One line in "Before you load the trailer". */
export const attentionItem = z.object({
  itemId: uuid,
  itemName: z.string(),
  who: z.string(),
  note: z.string(),
  status,
});
export type AttentionItem = z.infer<typeof attentionItem>;

export const carSummary = z.object({
  car: carDto,
  tally: z.object({ good: z.number().int(), due: z.number().int(), over: z.number().int() }),
  seasonPasses: z.number().int(),
  totalPasses: z.number().int(),
});
export type CarSummary = z.infer<typeof carSummary>;

export const dashboardResponse = z.object({
  garageName: z.string(),
  nextEvent: eventDto.nullable(),
  /** Due or overdue before the next event — what stops you loading the trailer. */
  attention: z.array(attentionItem),
  cars: z.array(carSummary),
  upcoming: z.array(timelineOccurrence),
  certs: z.array(certOccurrence),
  today: isoDate,
});
export type DashboardResponse = z.infer<typeof dashboardResponse>;

// ---------------------------------------------------------------------------
// History — the all-time record (the timeline's backward-looking twin)
// ---------------------------------------------------------------------------

/**
 * The season timeline forecasts *forward* from the projection engine; history
 * is the plain factual record *backward*, and it needs no engine at all — it is
 * the events that ran and the services that were logged, read straight from the
 * tables. Two separate lists rather than one pre-merged spine: an event and a
 * service are genuinely different rows with different fields, and the client
 * interleaves them by date the same way `toBlocks` does for the forecast.
 */

/** A past event on the history spine — what actually ran (or was scheduled). */
export const historyEvent = z.object({
  id: uuid,
  date: isoDate,
  name: z.string(),
  track: z.string().nullable(),
  carId: uuid,
  carName: z.string(),
  driverName: z.string().nullable(),
  status: eventStatus,
  /** actual passes/miles once run, the estimates until then. */
  passes: z.number().int(),
  miles: z.number().int(),
  /** True while still `planned` — the numbers are estimates, not what was run. */
  estimated: z.boolean(),
});
export type HistoryEvent = z.infer<typeof historyEvent>;

/** A logged service on the history spine. */
export const historyService = z.object({
  id: uuid,
  itemId: uuid,
  itemName: z.string(),
  /** The car's nickname, or "Driver · Name" for gear — who it hangs off. */
  ownerLabel: z.string(),
  /** The car it belongs to, for the timeline's per-car filter; null for gear. */
  carId: uuid.nullable(),
  servicedAt: isoDate,
  byUserName: z.string(),
  atPasses: z.number().int().nullable(),
  atMiles: z.number().int().nullable(),
  cost: z.number().nullable(),
  notes: z.string().nullable(),
  /** The event it was logged against, by name, if any. */
  eventName: z.string().nullable(),
});
export type HistoryService = z.infer<typeof historyService>;

export const historyResponse = z.object({
  events: z.array(historyEvent),
  services: z.array(historyService),
  today: isoDate,
});
export type HistoryResponse = z.infer<typeof historyResponse>;

/**
 * The public, unauthenticated view (§5, §11).
 *
 * Deliberately a *separate* schema rather than a filtered `DashboardResponse`:
 * a shared shape with fields stripped at runtime is one forgotten `delete`
 * away from leaking. Here, there is no member list and no PII to forget — the
 * types simply have nowhere to put them.
 */
/**
 * A part on the public view, shown only when the owner has opted into exposing
 * maintenance detail. This is the build sheet — name, part number, where to buy
 * — so it is gated behind `publicShowMaintenance` and absent otherwise.
 */
export const publicPart = z.object({
  name: z.string(),
  partNumber: z.string().nullable(),
  system: z.string(),
  buyUrl: z.string().nullable(),
  isSafety: z.boolean(),
});
export type PublicPart = z.infer<typeof publicPart>;

export const publicGarageResponse = z.object({
  garageName: z.string(),
  /** True when the owner has opted to expose part numbers / reorder links. */
  showMaintenance: z.boolean(),
  cars: z.array(
    z.object({
      id: uuid,
      nickname: z.string(),
      year: z.number().int().nullable(),
      make: z.string().nullable(),
      model: z.string().nullable(),
      motorCode: z.string().nullable(),
      powerAdder: z.string().nullable(),
      transmission: z.string().nullable(),
      raceClass: z.string().nullable(),
      useType: z.string(),
      imageUrl: z.string().nullable(),
      tally: z.object({ good: z.number().int(), due: z.number().int(), over: z.number().int() }),
      /** Present only when `showMaintenance` — the car's parts, for reference. */
      parts: z.array(publicPart),
    }),
  ),
  events: z.array(
    z.object({
      id: uuid,
      carId: uuid,
      date: isoDate,
      track: z.string().nullable(),
      name: z.string(),
      estPasses: z.number().int(),
      estMiles: z.number().int(),
    }),
  ),
  occurrences: z.array(timelineOccurrence),
  certs: z.array(certOccurrence),
  today: isoDate,
});
export type PublicGarageResponse = z.infer<typeof publicGarageResponse>;

export const publicTokenParam = z.object({
  token: z.string().min(16).max(64),
});
export type PublicTokenParam = z.infer<typeof publicTokenParam>;
